import React, { useState } from 'react';
import { Box, Button, IconButton, Tooltip, Typography, FormHelperText, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { UploadFileOutlined, VisibilityOutlined, DeleteOutline } from '@mui/icons-material';
import { useField, useFormikContext } from 'formik';
import { REACT_APP_CDN_URL } from '../../../../../../../../utils/base-urls';

interface FileUploadFieldProps {
    name: string;
    label: string;
    accept?: string;
}

const FileUploadField: React.FC<FileUploadFieldProps> = ({ name, label, accept = 'application/pdf' }) => {
    const { setFieldValue, values } = useFormikContext<any>();
    const [, meta] = useField(name);
    const file = values[name];

    const showError = meta.touched && Boolean(meta.error);

    const [openDialog, setOpenDialog] = useState(false);

    const [fileURL, setFileURL] = useState<string | null>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.currentTarget.files?.[0];
        if (selectedFile) {
            setFieldValue(name, selectedFile);
        }
    };

    const getFileUrl = (path: string) => {
        if (/^https?:\/\//i.test(path)) return path;
        return `${REACT_APP_CDN_URL}${path}`;
    };

    const isPdfFile = (file: File | string) => {
        if (file instanceof File) return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
        return file.toLowerCase().endsWith('.pdf');
    };

    const getDisplayFileName = (file: File | string) => {
        if (file instanceof File) return file.name;
        return file.split('/').pop() || 'Uploaded file';
    };

    const handleViewFile = (file: File | string) => {
        if (file instanceof File) {
            const fileUrl = URL.createObjectURL(file);
            setFileURL(fileUrl);
            setOpenDialog(true);
            return;
        }
        // For existing files (string paths from API), open in new tab —
        // S3/CDN responses commonly block iframe embedding via X-Frame-Options
        // or force download via Content-Disposition, so the in-dialog preview
        // would render blank.
        const url = getFileUrl(file);
        if (url) window.open(url, "_blank", "noopener,noreferrer");
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setFileURL(null);
    };

    return (
        <Box display="flex" flexDirection="column" mt={1}>
            <Box display="flex" alignItems="center" gap={1}>
                <Button
                    endIcon={<UploadFileOutlined />}
                    variant="contained"
                    component="label"
                    size="small"
                    color={showError ? 'error' : 'primary'}
                >
                    {label}
                    <input
                        type="file"
                        hidden
                        accept={accept}
                        onChange={handleFileChange}
                    />
                </Button>
                {file && (
                    <>
                        {/* <Tooltip title={getDisplayFileName(file)} arrow>
                            <Typography
                                variant="body2"
                                sx={{
                                    maxWidth: 150,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {getDisplayFileName(file)}
                            </Typography>
                        </Tooltip> */}
                        <Tooltip title="View uploaded file" arrow>
                            <IconButton onClick={() => handleViewFile(file)} color="primary">
                                <VisibilityOutlined />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Remove file" arrow>
                            <IconButton
                                onClick={() => setFieldValue(name, null)}
                                color="error"
                            >
                                <DeleteOutline />
                            </IconButton>
                        </Tooltip>
                    </>
                )}
            </Box>
            {showError && (
                <FormHelperText error sx={{ ml: 1 }}>
                    {meta.error}
                </FormHelperText>
            )}
            <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="md">
                <DialogTitle>View Uploaded File</DialogTitle>
                <DialogContent>
                    {file && isPdfFile(file) ? (
                        <iframe
                            src={fileURL ?? ''}
                            width="100%"
                            height="500px"
                            title="PDF Viewer"
                            style={{ border: 'none' }}
                        />
                    ) : (
                        <img src={fileURL ?? ''} alt="uploaded-file" width="100%" />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default FileUploadField;
